import React, {useEffect, useImperativeHandle,forwardRef, useState} from 'react';
import { Editor } from '@tinymce/tinymce-react';
import API from '../../utils/ApiParams';
import LibraryList from "../library/LibraryList";
import "./Editor.css";

/* options : 1 = full
             2 = Only bold, italic, underline, upper/lower case, erase style and history
             3 = Only upper/lower case, emoji, erase style and history
*/
function TinyEditor({maxChars=0, placeholder, handleChange, oldContent, options=1,
                    readOnly=false, height= 500}, ref) {
    const [showLibrary,setShowLibrary] = useState(false);
    const [content, setContent] = useState(oldContent?oldContent:"");
    const [disabled, setDisabled] = useState(readOnly);
    useEffect(()=>{
        if(oldContent !== null){
            setContent(oldContent);
            handleChange(oldContent);
        }
        // Only resync when the loaded content itself changes; `handleChange`
        // is redefined on every parent render and must NOT retrigger this,
        // otherwise each keystroke resets the editor back to oldContent.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [oldContent])
    useEffect(()=>{
        setDisabled(readOnly);
    }, [readOnly])
    useImperativeHandle(ref, () => ({
        uploadImages : () =>{
            return upload();
        }
    }))
    async function upload(){
        await window.tinymce.activeEditor.uploadImages().then(data=>{
           // handleChange(window.tinymce.activeEditor.getContent());
        });
    }
    function handleEditorChange (newContent, editor) {
        setContent(newContent);
        handleChange(newContent);
    }
    function handleOnPaste(event, editor) {
        const pastedContent = event.clipboardData.getData("text");
        // -13 because of default pasted content in mce
        const contentLength = editor.plugins.wordcount.body.getCharacterCount()-13;
        if(maxChars !== 0 && (contentLength + pastedContent.length > maxChars))
            return false;
        return true;
    }
    function handleKeyDown(event, editor) {
        const allowedKeys = [8, 37, 38, 39, 40, 46]; // backspace, delete and cursor keys
        const contentLength = editor.plugins.wordcount.body.getCharacterCount();
        if(allowedKeys.indexOf(event.keyCode) !== -1) return true;
        if (maxChars !== 0 && contentLength+1 > maxChars){
            return false;
        }
        return true;
    }
    function handleChangeLibraryImage(image) {
        window.tinymce.activeEditor.insertContent(
            `<img src=${image.url} alt=""/>`
        )
        setShowLibrary(false);
    }
    function handleUppercase(editor) {
        function decodeHTMLEntities(text) {
            var textArea = document.createElement('textarea');
            textArea.innerHTML = text;
            return textArea.value;
        }
        const selectedContent = decodeHTMLEntities(editor.selection.getContent());
        editor.selection.setContent(selectedContent.toUpperCase());
    }
    function handleLowercase(editor) {
        const selectedContent = editor.selection.getContent().toLowerCase();
        editor.selection.setContent(selectedContent);
    }
    async function handleImageUpload(blobInfo, success, failure) {
        let imageFile = new FormData();
        imageFile.append("file", blobInfo.blob());
        try {
            const response = await API.post("media_objects", imageFile,
                {
                    headers:{"Content-type" : "multipart/form-data"}
                })
            success(process.env.REACT_APP_API_URL+"images/"+response.data.id+"/"+response.data.name);
        } catch (error) {
            //handleResponseError(error);
            return;
        }
    }
    
    function getToolbarOptions() {
        switch (options) {
            case 1:
                return ['undo redo | bold italic underline strikethrough subscript superscript capitalize|\
                     fontsizeselect formatselect | forecolor backcolor |\
                     bullist numlist outdent indent alignleft aligncenter alignright alignjustify | \
                     table link media image emoticons| library removeformat'];
            case 2:
                return ['undo redo | bold italic underline capitalize| removeformat'];
            case 3:
                return ['undo redo | capitalize emoticons| removeformat'];
            default:
                return ['undo redo | bold italic underline strikethrough subscript superscript capitalize|\
                     fontsizeselect formatselect | forecolor backcolor |\
                     bullist numlist outdent indent alignleft aligncenter alignright alignjustify | \
                     table link media image emoticons| library removeformat'];
        }
    }
    return (
        <div>
            <div className={"library-modal"} style={{"display":showLibrary?"flex":"none"}}>
                <div className={"library-focus"} onClick={()=>setShowLibrary(false)}/>
                <div className={"library-wrapper"}>
                    <LibraryList classNames={"resizeLibrary"} addImage={true} imageClick={handleChangeLibraryImage} cols={4} rows={1}/>
                </div>
            </div>
            {/*
            Options right click :
                contextmenu: "copy paste | link image inserttable | cell row column deletetable",
            */}
        <Editor
            apiKey={process.env.REACT_APP_TINYMCE_KEY}
            value={content}
            disabled={disabled}
            init={{
                height: height,
                placeholder: placeholder,
                language : 'fr_FR',
                language_url : process.env.PUBLIC_URL + "/langs/fr_FR.js",
                file_picker_types: 'image',
                file_picker_callback: function (cb, value, meta) {
                    var input = document.createElement('input');
                    input.setAttribute('type', 'file');
                    input.setAttribute('accept', 'image/*');
                    input.onchange = function () {
                        var file = this.files[0];

                        var reader = new FileReader();
                        reader.onload = function () {
                            var id = 'blobid' + (new Date()).getTime();
                            var blobCache =  window.tinymce.activeEditor.editorUpload.blobCache;
                            var base64 = reader.result.split(',')[1];
                            var blobInfo = blobCache.create(id, file, base64);
                            blobCache.add(blobInfo);

                            cb(blobInfo.blobUri(), { title: file.name });
                        };
                        reader.readAsDataURL(file);
                    };

                    input.click();
                },
                automatic_uploads: false,
                images_upload_handler:handleImageUpload,
                menubar: false,
                plugins: [
                    'advlist autolink lists link image charmap print preview anchor',
                    'searchreplace visualblocks code fullscreen',
                    'insertdatetime media table paste code help wordcount table image ',
                    'link media emoticons'
                ],
                contextmenu: false,
                toolbar: getToolbarOptions(),
                paste_postprocess: (plugin, args) => {
                    args.node.querySelectorAll("table").forEach(tableNode => {
                        tableNode.setAttribute("border", "1");
                    });
                },
                paste_data_images: true,
                fontsize_formats: "8pt 9pt 10pt 11pt 12pt 14pt 18pt 24pt 30pt 36pt 48pt 60pt 72pt 96pt",
                font_formats:"DM Sans=DM Sans",
                block_formats: 'Normal=p;Titre=h1;Sous-titre=h2;',
                color_map: [
                    '000000', 'Noir',
                    'FFFFFF', 'Blanc',
                    '8F8278', 'Gris',
                    'DC052C', 'Rouge',
                    'FF052C', 'Général',
                    '8D3E1D', 'Achats',
                    '9EBFC5', 'Logistique',
                    '82C087', 'Commercial',
                    'E9AE5B', 'Restauration Services',
                ],
                custom_colors: false,
                content_style: "@import url('https://fonts.googleapis.com/css2?family=DM+Sans&display=swap'); @import url('https://fonts.googleapis.com/css2?family=DM+Sans&family=Lato&display=swap'); body{font-family:DM Sans};",
                setup: function (editor) {
                    editor.ui.registry.addIcon('library', '<svg width="20px" height="20px" viewBox="0 0 512 427" version="1.1" xmlns="http://www.w3.org/2000/svg">\n' +
                        '        <g id="Icon/General/Photo" transform="translate(0.000000, -43.000000)" fill="black">\n' +
                        '            <path d="M464,192.332031 C490.476562,192.332031 512,213.878906 512,240.332031 L512,421.667969 C512,448.121094 490.476562,469.667969 464,469.667969 L218.667969,469.667969 C192.191406,469.667969 170.667969,448.121094 170.667969,421.667969 L170.667969,416.332031 C170.667969,407.5 177.835938,400.332031 186.667969,400.332031 C195.5,400.332031 202.667969,407.5 202.667969,416.332031 L202.667969,421.667969 C202.667969,430.476562 209.835938,437.667969 218.667969,437.667969 L464,437.667969 C472.832031,437.667969 480,430.476562 480,421.667969 L480,240.332031 C480,231.523438 472.832031,224.332031 464,224.332031 L458.667969,224.332031 C449.835938,224.332031 442.667969,217.164062 442.667969,208.332031 C442.667969,199.5 449.835938,192.332031 458.667969,192.332031 L464,192.332031 Z M379.777344,43 C405.632812,43 426.667969,64.011719 426.667969,89.871094 L426.667969,316.109375 C426.667969,341.988281 405.632812,363 379.796875,363 L46.871094,363 C27.9626114,363 11.6255717,351.745493 4.22401445,335.572956 C1.25624821,332.076857 0.0313274883,327.607067 0.548891728,323.306423 C0.187325893,320.966345 8.52651283e-14,318.569211 8.52651283e-14,316.128906 L8.52651283e-14,89.871094 C8.52651283e-14,64.011719 21.035156,43 46.871094,43 L379.777344,43 Z M288.171875,166.667969 C287.253906,166.667969 285.484375,166.925781 284.097656,168.609375 L193.621094,277.238281 C190.742188,280.675781 186.539062,282.765625 182.058594,282.980469 C177.492188,283.511719 173.207031,281.484375 170.027344,278.308594 L131.839844,240.121094 C129.75,238.03125 126.25,238.03125 124.160156,240.121094 L37.0384615,327.260464 C39.6620545,329.575727 43.1062324,330.980469 46.871094,330.980469 L379.777344,330.980469 C387.96875,330.980469 394.644531,324.324219 394.644531,316.109375 L394.644,288.028 L292.265625,168.589844 C290.835938,166.902344 289.75,167.03125 288.171875,166.667969 Z M379.777344,75 L46.871094,75 C38.675781,75 32,81.65625 32,89.871094 L32,287.025 L101.546875,217.464844 C116.136719,202.871094 139.863281,202.871094 154.453125,217.464844 L180.265625,243.257812 L259.5,148.128906 C266.582031,139.640625 276.96875,134.710938 288,134.667969 L288.191406,134.667969 C299.15625,134.667969 309.503906,139.449219 316.609375,147.8125 L394.644,238.842 L394.644531,89.871094 C394.644531,81.65625 387.96875,75 379.777344,75 Z M106.667969,85.667969 C130.199219,85.667969 149.332031,104.800781 149.332031,128.332031 C149.332031,151.863281 130.199219,171 106.667969,171 C83.136719,171 64,151.863281 64,128.332031 C64,104.800781 83.136719,85.667969 106.667969,85.667969 Z M106.667969,117.667969 C100.777344,117.667969 96,122.464844 96,128.332031 C96,134.199219 100.777344,139 106.667969,139 C112.554688,139 117.332031,134.199219 117.332031,128.332031 C117.332031,122.464844 112.554688,117.667969 106.667969,117.667969 Z" id="Combined-Shape"></path>\n' +
                        '        </g>\n' +
                        '    </svg>');
                    editor.ui.registry.addIcon('capitalize', '<svg version="1.1" id="Capa_1" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px"\n' +
                        '\t width="20px" height="20px" viewBox="0 0 28.496 28.496" style="enable-background:new 0 0 28.496 28.496;"\n' +
                        '\t xml:space="preserve">\n' +
                        '<g>\n' +
                        '\t<g>\n' +
                        '\t\t<path d="M11.44,15.353c0-2.99-1.352-5.486-5.59-5.486c-2.314,0-4.056,0.624-4.94,1.118l0.728,2.548\n' +
                        '\t\t\tc0.832-0.52,2.21-0.962,3.51-0.962c1.95,0,2.314,0.962,2.314,1.639v0.183C2.964,14.365,0,15.952,0,19.253\n' +
                        '\t\t\tc0,2.028,1.534,3.9,4.108,3.9c1.508,0,2.808-0.547,3.64-1.562h0.078l0.234,1.274h3.562c-0.13-0.702-0.182-1.872-0.182-3.096\n' +
                        '\t\t\tV15.353z M7.592,18.239c0,0.232-0.026,0.468-0.078,0.676c-0.26,0.807-1.066,1.456-2.054,1.456c-0.884,0-1.56-0.494-1.56-1.508\n' +
                        '\t\t\tc0-1.534,1.612-2.028,3.692-2.003V18.239z"/>\n' +
                        '\t\t<path d="M23.062,5.343h-5.201l-5.355,17.524h4.108l1.247-4.498h5.019l1.353,4.498h4.264L23.062,5.343z M18.434,15.404l1.041-3.718\n' +
                        '\t\t\tc0.285-1.014,0.545-2.34,0.806-3.38h0.052c0.26,1.04,0.572,2.34,0.884,3.38l1.093,3.718H18.434z"/>\n' +
                        '\t\t<polygon points="8.025,6.584 1.999,6.584 1.999,6.833 1.999,7.461 1.999,7.709 8.025,7.709 8.025,8.95 12.442,7.146 8.025,5.343 \n' +
                        '\t\t\t\t\t"/>\n' +
                        '\t</g>\n' +
                        '</g>\n' +
                        '</svg>');
                    editor.ui.registry.addButton("library",{
                        icon:"library",
                        tooltip: "Sélectionner une image depuis la médiathèque",
                        onAction: function () {
                            setShowLibrary(true);
                        }
                    })
                    editor.ui.registry.addMenuButton('capitalize', {
                        icon: 'capitalize',
                        fetch: function (callback) {
                            var items = [
                                {
                                    type: 'menuitem',
                                    text: 'Majuscule',
                                    onAction: function (_) {
                                        handleUppercase(editor)
                                    }
                                },
                                {
                                    type: 'menuitem',
                                    text: 'Minuscule',
                                    onAction: function (_) {
                                        handleLowercase(editor)
                                    }
                                },
                            ];
                            callback(items);
                        }
                    });
                }
            }}
            onPaste={handleOnPaste}
            onKeyDown={handleKeyDown}
            onEditorChange={handleEditorChange}
        />
        </div>
    );
}
export default forwardRef(TinyEditor)